import { Typography } from "@material-ui/core";
import dayjs from "dayjs";
import React from "react";
import { useTranslation } from "react-i18next";

import useDateFormatter from "../../hooks/useDateFormatter";

// ─── Helpers de período ───────────────────────────────────────────────────────

const PERIODS = [
	{ key: "morning",   labelKey: "Morning"   },
	{ key: "afternoon", labelKey: "Afternoon" },
	{ key: "evening",   labelKey: "Evening"   },
];

function getLocalHour(startUTC, timeZone) {
	const hhmm = dayjs(startUTC).utc().tz(timeZone ?? "UTC").format("HH:mm");
	return parseInt(hhmm.split(":")[0], 10);
}

function classifyPeriod(hour) {
	if (hour < 12) return "morning";
	if (hour < 18) return "afternoon";
	return "evening";
}

// ─── Componente ───────────────────────────────────────────────────────────────

const TimeOptionsDiv = ({
	options,
	onSelect,
	selected,
	professional,
	service,
	professionals,
	listProfessionals,
	allowSelectProfessional,
	professionalsOfService,
	business,
	unavailableSlots = [],
}) => {
	const { t } = useTranslation();
	const { formatTime } = useDateFormatter();

	const timeZone = business?.timeZone ?? "UTC";

	// ── Coleta slots disponíveis ────────────────────────────────────────────────

	const availableEntries = (() => {
		const seen = new Set();
		const entries = [];

		if (professionals && professionals.length > 0) {
			// Modo multi-profissional: usa professionals[].schedule
			professionals
				.filter((p) => p.schedule && p.schedule.length > 0)
				.forEach((prof) => {
					prof.schedule.forEach((scheduleItem) => {
						const availability = scheduleItem[0];
						if (availability?.start && !seen.has(availability.start)) {
							seen.add(availability.start);
							entries.push({ availability, professional: prof, scheduleItem });
						}
					});
				});
		} else if (options && options.length > 0) {
			// Modo profissional único: usa options diretamente
			options.forEach((item) => {
				const availability = item[0];
				if (availability?.start && !seen.has(availability.start)) {
					seen.add(availability.start);
					entries.push({ availability, professional, scheduleItem: item });
				}
			});
		}

		return { entries, seen };
	})();

	// ── Coleta slots desabilitados (agendados) ─────────────────────────────────
	// Deduplica por start time: unavailability da API pode ter duplicatas

	const disabledEntries = (() => {
		const seenDisabled = new Set();
		return (unavailableSlots ?? [])
			.filter((slot) => {
				if (!slot?.start) return false;
				if (availableEntries.seen.has(slot.start)) return false; // já aparece como disponível
				if (seenDisabled.has(slot.start)) return false;          // duplicata
				seenDisabled.add(slot.start);
				return true;
			})
			.map((slot) => ({ availability: slot, disabled: true }));
	})();

	// ── Agrupa por período ─────────────────────────────────────────────────────

	const allEntries = [
		...availableEntries.entries.map((e) => ({ ...e, disabled: false })),
		...disabledEntries,
	].sort((a, b) => new Date(a.availability.start) - new Date(b.availability.start));

	const groups = { morning: [], afternoon: [], evening: [] };
	allEntries.forEach((entry) => {
		const hour   = getLocalHour(entry.availability.start, timeZone);
		const period = classifyPeriod(hour);
		groups[period].push(entry);
	});

	// ── Lógica de auto-seleção (comportamento existente preservado) ───────────

	const isAutoSelected =
		!professionalsOfService && professional?.schedule?.length === 1;

	// ── Render de um card de horário ───────────────────────────────────────────

	const renderCard = (entry) => {
		const { availability, professional: prof, disabled } = entry;

		const isSelected =
			!disabled &&
			prof?.id === selected?.professional?.id &&
			availability?.start === selected?.time?.start;

		const isNotClickable = disabled || isAutoSelected;

		const label = formatTime(
			dayjs(availability.start).utc().tz(timeZone).format("HH:mm")
		);

		return (
			<div
				key={availability.start}
				data-disabled={disabled ? "true" : "false"}
				onClick={() => {
					if (isNotClickable) return;
					onSelect({ time: availability, professional: prof ?? professional, service });
				}}
				style={{
					color:           disabled ? "#B0A49C" : isSelected ? "white" : "#333333",
					backgroundColor: isSelected ? "#672E37" : "#EDE8E3",
					borderRadius:    "10px",
					display:         "flex",
					alignItems:      "center",
					justifyContent:  "center",
					minWidth:        "90px",
					height:          "44px",
					padding:         "0 20px",
					cursor:          isNotClickable ? "not-allowed" : "pointer",
					textDecoration:  disabled ? "line-through" : "none",
					flexShrink:      0,
					fontSize:        "14px",
					fontWeight:      600,
					fontFamily:      "CircularStd, sans-serif",
				}}
			>
				{label}
			</div>
		);
	};

	// ── Render principal ───────────────────────────────────────────────────────

	return (
		<div style={{ width: "100%" }}>
			{PERIODS.map(({ key, labelKey }) => {
				const slots = groups[key];
				if (!slots || slots.length === 0) return null;

				return (
					<div key={key} data-period={key} style={{ marginBottom: 8 }}>
						<Typography
							style={{
								fontWeight:    600,
								fontSize:      12,
								color:         "#666666",
								paddingBottom: 6,
								fontFamily:    "CircularStd, sans-serif",
								textTransform: "uppercase",
								letterSpacing: "0.5px",
							}}
						>
							{t(labelKey)}
						</Typography>
						<div
							style={{
								display:   "flex",
								flexWrap:  "wrap",
								gap:       8,
								width:     "100%",
							}}
						>
							{slots.map(renderCard)}
						</div>
					</div>
				);
			})}
		</div>
	);
};

export default TimeOptionsDiv;
